import React, { useCallback } from 'react';
import { Button, Checkbox, ControlLink, Input, LoadingBalls, Overlay, Stack, Text } from '@moda/om';
import { noop, useForm } from '@moda/portal-stanchions';
import { useCountry } from '../CountrySelector';
import { usePasswordlessAuth } from '../../hooks/usePasswordlessAuth';
import { AuthPayloadSuccess } from '../../lib/authenticate';
import { hrefFor } from '../../routers';

type PasswordlessRegistrationFormValues = {
  registerEmailList: boolean;
  registerFirstName: string;
  registerLastName: string;
  registerRemember: boolean;
};

const VALIDATIONS = {
  registerFirstName: {
    required: {
      message: 'Please enter first name.',
      value: true
    }
  },
  registerLastName: {
    required: {
      message: 'Please enter last name.',
      value: true
    }
  }
};

type PasswordlessRegistrationStepProps = {
  authorizationToken?: string | null;
  email: string;
  registrationToken: string;
  cancelLabel?: string;
  defaultEmailListChecked?: boolean;
  message?: string;
  remember?: boolean;
  onAuth: (authUser?: AuthPayloadSuccess) => void;
  onCancel: () => void;
  onComplete?: () => void;
  title?: string;
};

export const PasswordlessRegistrationStep: React.FC<PasswordlessRegistrationStepProps> = ({
  authorizationToken,
  cancelLabel = 'Cancel',
  defaultEmailListChecked = true,
  email,
  message = 'Complete your account details to continue.',
  onAuth,
  onCancel,
  onComplete,
  registrationToken,
  remember = true,
  title = 'Complete Registration'
}) => {
  const { country } = useCountry();
  const { error, loading, completeRegistration } = usePasswordlessAuth();

  const handleRegistrationSubmit = useCallback(
    (values: PasswordlessRegistrationFormValues) => {
      completeRegistration({
        authorizationToken,
        email,
        registrationToken,
        firstName: values.registerFirstName,
        lastName: values.registerLastName,
        remember: values.registerRemember,
        optedIn: values.registerEmailList,
        countryCode: country.alpha2Code
      })
        .then(result => {
          if (result.type !== 'authenticated') return result;

          onAuth(result.user);
          onComplete?.();

          return result;
        })
        .catch(noop);
    },
    [
      authorizationToken,
      completeRegistration,
      country.alpha2Code,
      email,
      onAuth,
      onComplete,
      registrationToken
    ]
  );

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit: handleRegistrationSubmit,
    validations: VALIDATIONS
  });

  return (
    <>
      <form onSubmit={handleSubmit} data-testid="signInSection">
        {title && <div className="Authentication__title">{title}</div>}
        {message && <div className="Authentication__message">{message}</div>}
        {error?.message && (
          <div className="Authentication__error" role="alert">
            {error.message}
          </div>
        )}

        <Stack space={5}>
          <div className="Authentication__fields">
            <div className="Authentication__alignedInputs">
              <Input
                className="Authentication__input"
                error={fieldErrors.registerFirstName?.message || error != null}
                id="registerFirstName"
                data-testid="registerFirstName"
                placeholder="First Name"
                type="text"
                maxLength={50}
              />

              <Input
                className="Authentication__input"
                error={fieldErrors.registerLastName?.message || error != null}
                id="registerLastName"
                data-testid="registerLastName"
                placeholder="Last Name"
                type="text"
                maxLength={50}
              />
            </div>

            <div className="Authentication__checkboxes">
              <Checkbox
                className="Authentication__checkbox"
                defaultChecked={remember}
                id="registerRemember"
                data-testid="registerRemember"
                type="checkbox"
              >
                Remember me
              </Checkbox>

              <Checkbox
                className="Authentication__checkbox"
                defaultChecked={defaultEmailListChecked}
                id="registerEmailList"
                data-testid="registerEmailList"
                type="checkbox"
              >
                Put me on the email list for style inspiration special access
              </Checkbox>
            </div>
          </div>

          <Stack space={2}>
            <Button
              className="Authentication__button Authentication__button--sign-in"
              type="submit"
              data-testid="signInSubmit"
            >
              COMPLETE REGISTRATION
            </Button>

            <Button
              className="Authentication__button Authentication__button--cancel-sign-in"
              onClick={onCancel}
              secondary
              type="button"
            >
              {cancelLabel}
            </Button>
          </Stack>

          <Text>
            By clicking Complete Registration or registering an account with us, you agree to our{' '}
            <ControlLink
              to={hrefFor.TermsAndConditionsPage()}
              className="Authentication__terms"
              underlined
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms Conditions
            </ControlLink>
            <Text> and </Text>
            <ControlLink
              to={hrefFor.PrivacyPolicyPage()}
              className="Authentication__privacy"
              underlined
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy.
            </ControlLink>
          </Text>
        </Stack>
      </form>

      <Overlay className="Authentication__overlay" show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
